import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { format } from 'app/shared/utils/string.util';
import { supportAzureBlobType } from 'app/registries/linked-service/azure-storage-shared-fields';
import { azureResourceSelectMethodOptions } from 'app/registries/consts/metadata-consts';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { HDInsightClusterAuthTypes, CredentialTypeOptions } from 'shell-models-lib';

const hdiStorageOptions = [
    {
        label: stringResources.Hdi_StorageOption_AzureBlob,
        value: 'blob',
        displayName: stringResources.Hdi_StorageOption_AzureBlob
    },
    {
        label: stringResources.Hdi_StorageOption_DataLake,
        value: 'adls',
        displayName: stringResources.Hdi_StorageOption_DataLake
    }
];

const hdiClusterAuthOptionsManualSelection: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: HDInsightClusterAuthTypes.MSIAuthentication
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: HDInsightClusterAuthTypes.UserAssignedManagedIdentity
    },
    {
        label: stringResources.AuthenticationType_Select_Basic_Option,
        value: HDInsightClusterAuthTypes.BasicAuth
    }
];

const usernameTextbox: ControlMetadata = {
    type: 'textbox',
    bind: 'userName',
    label: stringResources.ModelName_UserName,
    required: true,
    validators: [
    { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
}

const passwordTextbox: ControlMetadata ={
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'password',
        label: stringResources.ModelName_Password,
        required: true,
        isLabelVertical: true
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Hdi_DisplayName),
    stringResources.ModelName_Password)
}

const managedIdentityBasicAuthDropdown: ControlMetadata = {
    type: 'select-switch',
    bind: 'clusterAuthType',
    label: stringResources.Hdi_AuthenticationType,
    options: hdiClusterAuthOptionsManualSelection,
    isLabelVertical: true,
    defaultValue: '',
    required: true,
    description: stringResources.Hdi_AuthenticationType_Tooltip,
    cases: [
            {
                caseValue: HDInsightClusterAuthTypes.UserAssignedManagedIdentity,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            },
            {
                caseValue: HDInsightClusterAuthTypes.MSIAuthentication,
                children: [
                    {
                        type: 'management-identity-info',
                        resourceType: stringResources.Hdi_DisplayName,
                        link: stringResources.ModelName_HDInsightClusterDocLink
                    }
                ]
            },
            {
                caseValue: HDInsightClusterAuthTypes.BasicAuth,
                children: [
                    usernameTextbox,
                    passwordTextbox
                ]
            }
        ],
        validators: [
            { type: 'required', errorMessage: '' }
        ]
}

const clusterUriTextbox: ControlMetadata = {
    type: 'textbox',
    bind: 'clusterUri',
    label: stringResources.ModelName_HDInsightClusterUri,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
}

const clusterUriWithAllAllowedAuth: ControlMetadata[] = [
    clusterUriTextbox,
    managedIdentityBasicAuthDropdown
];

export const hdInsightMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.hdInsight.linkedServiceType,
    displayName: stringResources.Hdi_DisplayName,
    fullName: stringResources.Hdi_FullName,
    notShowInTypeSelection: !__IS_FABRIC_DPE__,
    icon: {
        className: 'svg-i-100x100-azure_hdinsight'
    },
    supportedInTrident: true,
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'AzureHDInsightCluster' as ResourceKind
    },
    supportOPDG: GlobalVariables.enableOPDG,
    isCompute: true,
    isFirstClass: true,
    category: ['Compute'],
    copySinkTypes: [],
    copySourceTypes: [],
    controls: [
        {
            type: 'radio-switch',
            nested: 1,
            label: stringResources.AccountSelectionMethodLabel,
            options: azureResourceSelectMethodOptions,
            isLabelVertical: true,
            defaultValue: '{clusterUri != null ? "manual" : "azure"}',
            cases: [
                {
                    caseValue: 'azure',
                    children: [
                        {
                            type: 'azure-hdinsight-account-select',
                            bind: GlobalVariables.enableHDIClusterMIAuth
                                ? 'clusterAuthObject'
                                : 'clusterUri',
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    ]
                },
                {
                    caseValue: 'manual',
                    children: GlobalVariables.enableHDIClusterMIAuth
                        ? clusterUriWithAllAllowedAuth
                        : [clusterUriTextbox]
                }
            ]
        },
        {
            type: 'radio-switch',
            nested: 1,
            label: stringResources.AzureStorage_LinkedServiceLabel,
            options: hdiStorageOptions,
            isLabelVertical: true,
            defaultValue: '{ (azureStorageLinkedService && azureStorageLinkedService.url) ? "adls" : "blob"}',
            cases: [
                {
                    caseValue: 'blob',
                    children: [
                        {
                            type: 'linkedservice-select-for-sidenav',
                            bind: 'azureStorageLinkedService',
                            linkedServiceTypes: supportAzureBlobType,
                            label: stringResources.AzureStorage_LinkedServiceLabel,
                            newLinkedServiceOptionText: stringResources.AddNew,
                            required: true,
                            validators: [{ type: 'required', errorMessage: '' }]
                        }
                    ]
                },
                {
                    caseValue: 'adls',
                    children: [
                        {
                            type: 'linkedservice-select-for-sidenav',
                            bind: 'azureStorageLinkedService',
                            linkedServiceTypes: [Metamodel.modelFor(AzureBlobFSLinkedService).name],
                            label: stringResources.AzureStorage_LinkedServiceLabel,
                            newLinkedServiceOptionText: stringResources.AddNew,
                            required: true,
                            validators: [{ type: 'required', errorMessage: '' }]
                        },
                        {
                            type: 'textbox',
                            bind: 'fileSystem',
                            label: stringResources.FileSystem_DisplayText,
                            required: true,
                            validators: [{ type: 'required', errorMessage: stringResources.Hdi_FileSystemRequired }],
                            isLabelVertical: true
                        }
                    ]
                }
            ]
        },
        ...(!GlobalVariables.enableHDIClusterMIAuth ? [usernameTextbox, passwordTextbox] : []),
        {
            type: 'checkbox',
            bind: 'isEspEnabled',
            label: stringResources.ModelName_HDInsightIsEspEnabled,
            labelPosition: 'right',
            description: stringResources.Hdi_ToolTip_esp,
            defaultValue: false
        }
    ]
};
